import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { BASELINE } from "../../util";
import axios from "axios";
import { UserContext } from "../../context/UserContext";
import CourseCard from "./CourseCard";
import { toast } from "react-hot-toast";
import AIChatModal from "./SAT/onlineclass/class_creation_modal";
import CourseLayout from "./SAT/onlineclass/class_main_ui";
import SatCourses from "./SAT/onlineclass/satcourse";
import { motion, AnimatePresence } from "framer-motion";
import { FiPlus, FiBookOpen, FiX, FiHash } from "react-icons/fi";
import { HiOutlineAcademicCap } from "react-icons/hi2";

// ── Join Class Modal ─────────────────────────────────────────────────────────
function JoinClassModal({ isOpen, onClose, onJoin }) {
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!code.trim()) return;
        setLoading(true);
        try {
            await onJoin(code.trim().toUpperCase());
            setCode("");
        } catch { } finally { setLoading(false); }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/40 backdrop-blur-sm"
                    onClick={e => { if (e.target === e.currentTarget) onClose(); }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 14, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.97 }}
                        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                        className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                                <FiHash size={14} className="text-blue-500" />
                                <h2 className="text-base font-bold text-slate-800">Join a Class</h2>
                            </div>
                            <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all">
                                <FiX size={16} />
                            </button>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                                    Class Code
                                </label>
                                <input
                                    type="text"
                                    value={code}
                                    onChange={e => setCode(e.target.value.toUpperCase())}
                                    placeholder="e.g. IT9AEO"
                                    autoFocus
                                    className="w-full rounded-xl px-4 py-2.5 text-sm font-mono text-slate-800 placeholder-slate-300 bg-slate-50 border border-slate-200 outline-none tracking-widest focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                                />
                            </div>
                            <p className="text-xs text-slate-400">
                                Ask your teacher for the 6-character class code.
                            </p>
                            <div className="flex justify-end gap-2 pt-1">
                                <button type="button" onClick={onClose}
                                    className="px-4 py-2 rounded-xl text-sm font-medium text-slate-500 bg-slate-100 hover:bg-slate-200 transition-all">
                                    Cancel
                                </button>
                                <button type="submit" disabled={!code.trim() || loading}
                                    className={`px-5 py-2 rounded-xl text-sm font-bold text-white transition-all ${
                                        code.trim() && !loading
                                            ? 'bg-blue-600 hover:bg-blue-500 shadow-sm'
                                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                    }`}>
                                    {loading ? "Joining…" : "Join Class"}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// ── Main Component ───────────────────────────────────────────────────────────
const StudentDashboard = ({ setdashboard, setshortid, courseSlug }) => {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const [classlst, setclasslst] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [section, setsection] = useState(9);
    const [registerclass, setregisterclass] = useState(false);

    const [isAIChatModalOpen, setIsAIChatModalOpen] = useState(false);
    const [currentView, setCurrentView] = useState('dashboard');
    const [activeCourse, setActiveCourse] = useState(null);

    const [coursesData, setCoursesData] = useState({ englishCourses: [], mathCourses: [] });
    const [loading, setLoading] = useState(true);

    const fetchCourses = async () => {
        try {
            const response = await axios.get(BASELINE + 'api/courses');
            if (response.data.success) {
                const english = response.data.courses.filter(c => c.type === 'eng');
                const math = response.data.courses.filter(c => c.type === 'math');
                setCoursesData({
                    englishCourses: transformToSubjects(english, 'English'),
                    mathCourses: transformToSubjects(math, 'Math'),
                });
            }
        } catch { }
    };

    const transformToSubjects = (apiCourses, tag) =>
        apiCourses.map(course => {
            let courseContent = null;
            if (course.context) { try { courseContent = JSON.parse(course.context); } catch { } }
            return {
                name: course.name, id: course.id, count: 0,
                isOpen: course.status === 'open', isSelected: false, tag,
                subTopics: [{
                    name: course.from || 'Content', count: 0, isSelected: false,
                    course: courseContent, toggle: function () { this.isSelected = !this.isSelected; },
                    originalData: course
                }]
            };
        });

    const [esubjects] = useState([
        { name: 'Information and Ideas', count: 0, isOpen: true, isSelected: false, tag: 'English', subTopics: [{ name: 'Inferences', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }, { name: 'Central Ideas and Details', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }, { name: 'Command of Evidence', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }] },
        { name: 'Craft and Structure', count: 0, isOpen: true, isSelected: false, tag: 'English', subTopics: [{ name: 'Words in Context', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }, { name: 'Text Structure and Purpose', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }, { name: 'Cross-Text Connections', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }] },
        { name: 'Expression of Ideas', count: 0, isOpen: true, isSelected: false, tag: 'English', subTopics: [{ name: 'Rhetorical Synthesis', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }, { name: 'Transitions', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }] },
        { name: 'Standard English Conventions', count: 0, isOpen: true, isSelected: false, tag: 'English', subTopics: [{ name: 'Boundaries', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }, { name: 'Form, Structure, and Sense', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }] },
    ]);

    const [msubjects] = useState([
        { name: 'Chapter 1', count: 0, isOpen: true, isSelected: false, tag: 'Math', subTopics: [{ name: 'Preliminary', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }] },
        { name: 'Chapter 2', count: 0, isOpen: true, isSelected: false, tag: 'Math', subTopics: [{ name: 'Exponents and Radicals', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }] },
        { name: 'Chapter 3', count: 0, isOpen: true, isSelected: false, tag: 'Math', subTopics: [{ name: 'Linear Equations', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }] },
        { name: 'Chapter 4', count: 0, isOpen: true, isSelected: false, tag: 'Math', subTopics: [{ name: 'Quadratics', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }] },
        { name: 'Chapter 5', count: 0, isOpen: true, isSelected: false, tag: 'Math', subTopics: [{ name: 'Functions', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }] },
        { name: 'Chapter 6', count: 0, isOpen: true, isSelected: false, tag: 'Math', subTopics: [{ name: 'Unit Conversion, Rates & Proportion', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }] },
        { name: 'Chapter 7', count: 0, isOpen: true, isSelected: false, tag: 'Math', subTopics: [{ name: 'Percent & Probability', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }] },
        { name: 'Chapter 8', count: 0, isOpen: true, isSelected: false, tag: 'Math', subTopics: [{ name: 'Systems of Equations', count: 0, isSelected: false, toggle: function () { this.isSelected = !this.isSelected; } }] },
    ]);

    const [useApiCourses] = useState(true);

    const handleregisterclass = async (code) => {
        try {
            await axios.post(BASELINE + 'register/class/by/student', { u_id: user.u_id, shortid: code });
            toast.success("Class joined!");
            setregisterclass(false);
            getclass();
        } catch (error) {
            if (error.response?.status === 400) toast.error("Already enrolled in this class.");
            else if (error.response?.status === 404) toast.error("Class not found. Check the code and try again.");
            else toast.error("Could not join the class. Please try again.");
            throw error;
        }
    };

    const getclass = () => {
        setLoading(true);
        axios.get(BASELINE + 'getclassbyuid', { params: { student_u_id: user.u_id } })
            .then(r => setclasslst(r.data.classes || []))
            .catch(e => console.error(e))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (user?.u_id) { getclass(); fetchCourses(); }
    }, [user?.u_id]);

    useEffect(() => {
        if (courseSlug) {
            const id = Number(courseSlug);
            if (!isNaN(id)) { setActiveCourse(id); setCurrentView('course'); }
        } else {
            setCurrentView('dashboard');
            setActiveCourse(null);
        }
    }, [courseSlug]);

    const handleAICourseCreated = (course) => { setIsAIChatModalOpen(false); setActiveCourse(course); setCurrentView('course'); };
    const handleSubTopicClick = (subTopic) => {
        if (!subTopic) return;
        navigate(`/main/sat/courses/${subTopic}`);
    };
    const handleBackToDashboard = () => { navigate('/main/sat/courses'); };

    if (currentView === 'course' && activeCourse) {
        return <CourseLayout courseData={activeCourse} courseId={typeof activeCourse === 'number' ? activeCourse : null} onBack={handleBackToDashboard} />;
    }

    return (
        <div className="h-full overflow-y-auto" style={{ background: "#eef2f7" }}>
            <div className="px-6 py-6 space-y-8">

                {/* ── Hero ─────────────────────────────────────────────────── */}
                <div className="rounded-xl overflow-hidden" style={{ background: "#0f2444" }}>
                    <div className="px-8 py-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <p className="text-slate-400 text-[11px] font-medium tracking-[0.18em] uppercase mb-1">Welcome back</p>
                            <h1 className="text-2xl font-bold text-white">{user?.first_name} <span className="text-white/50">{user?.last_name}</span></h1>
                            <p className="text-slate-400 text-sm mt-1">
                                {classlst.length > 0
                                    ? `Enrolled in ${classlst.length} class${classlst.length > 1 ? 'es' : ''}.`
                                    : "Join a class or explore courses below."}
                            </p>
                        </div>
                        <button
                            onClick={() => setregisterclass(true)}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-500 transition-colors shadow-sm self-start sm:self-auto shrink-0"
                        >
                            <FiPlus size={15} />
                            Join Class
                        </button>
                    </div>
                </div>

                {/* ── My Classes ───────────────────────────────────────────── */}
                <div>
                    <div className="flex items-center gap-3 mb-4">
                        <FiBookOpen size={13} className="text-slate-400" />
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.18em]">My Classes</span>
                        {classlst.length > 0 && (
                            <span className="text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">
                                {classlst.length}
                            </span>
                        )}
                        <div className="flex-1 h-px bg-slate-300/60" />
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="rounded-xl h-44 animate-pulse bg-white border border-slate-200" />
                            ))}
                        </div>
                    ) : classlst.length === 0 ? (
                        <div className="bg-white border border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center py-14 px-6 text-center">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-blue-50 border border-blue-200">
                                <HiOutlineAcademicCap size={22} className="text-blue-500" />
                            </div>
                            <p className="text-sm font-semibold text-slate-600 mb-1">No classes yet</p>
                            <p className="text-xs text-slate-400 mb-4">Ask your teacher for a class code to enroll.</p>
                            <button
                                onClick={() => setregisterclass(true)}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-500 transition-colors shadow-sm"
                            >
                                <FiPlus size={13} />
                                Join a Class
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {classlst.map((course, idx) => (
                                <CourseCard
                                    key={idx}
                                    {...course}
                                    course={course}
                                    setsection={setsection}
                                    setSelectedClass={setSelectedClass}
                                    role={user.role}
                                    setdashboard={setdashboard}
                                    setshortid={setshortid}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* ── SAT Courses ──────────────────────────────────────────── */}
                <div>
                    <div className="flex items-center gap-3 mb-4">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.18em]">SAT Courses</span>
                        <div className="flex-1 h-px bg-slate-300/60" />
                    </div>
                    <SatCourses
                        esubjects={useApiCourses && coursesData.englishCourses.length > 0 ? coursesData.englishCourses : esubjects}
                        msubjects={useApiCourses && coursesData.mathCourses.length > 0 ? coursesData.mathCourses : msubjects}
                        handleSubTopicClick={handleSubTopicClick}
                        handleAICourseCreated={handleAICourseCreated}
                    />
                </div>
            </div>

            <JoinClassModal isOpen={registerclass} onClose={() => setregisterclass(false)} onJoin={handleregisterclass} />
            <AIChatModal isOpen={isAIChatModalOpen} onClose={() => setIsAIChatModalOpen(false)} onComplete={handleAICourseCreated} esubjects={esubjects} msubjects={msubjects} />
        </div>
    );
};

export default StudentDashboard;
